import { useEffect, useState } from "react";
import "./style.css";
import {
  CloudUploadIcon,
  FileIcon,
  CloseIcon,
  ChevronRightIcon,
  RoundCrossIcon,
} from "../../../Assets/images/svgs";
import { Button, Tooltip } from "@chakra-ui/react";
import { ReactTable } from "../index";
import { ellipsisify } from "../../../Library/helpers";

/**
 * Reusable CSV Upload Component
 * Extracted from BulkMapDevToVeh for reusability
 * 
 * @param {Array<string>} requiredHeaders - Array of required CSV column headers
 * @param {Function} onDataProcessed - Callback when data is successfully processed (rows, invalidRows)
 * @param {number} maxRows - Maximum number of rows allowed (default: 100)
 * @param {string} templateName - Name for downloaded template file
 * @param {Function} customValidation - Optional custom validation function (row) => { isValid, errors }
 * @param {Array} columns - Table columns for preview (optional, auto-generated from headers if not provided)
 */
export default function CSVUploader({
  requiredHeaders = [],
  onDataProcessed,
  maxRows = 100,
  templateName = "Template.csv",
  customValidation,
  columns: customColumns,
}) {
  const [phase, setPhase] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [progress, setProgress] = useState(0);
  const [showModalPreview, setShowModalPreview] = useState(false);

  const stripBOM = (str) => (str && str.charCodeAt(0) === 0xfeff ? str.slice(1) : str);

  const parseCsvLine = (line) => {
    const result = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
        continue;
      }
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === "," && !inQuotes) {
        result.push(cur);
        cur = "";
        continue;
      }
      cur += ch;
    }
    result.push(cur);
    return result.map((f) => (f !== undefined && f !== null ? f.trim() : ""));
  };

  const validateHeadersExact = (headersArr) => {
    if (!Array.isArray(headersArr)) return false;
    if (headersArr.length < requiredHeaders.length) return false;
    for (let i = 0; i < requiredHeaders.length; i++) {
      if ((headersArr[i] || "").trim() !== requiredHeaders[i]) return false;
    }
    return true;
  };

  const parseCSVText = async (text) => {
    if (!text || !text.trim()) {
      setErrorMsg("Uploaded file seems empty!");
      setPhase("error");
      return;
    }

    const cleanedText = stripBOM(text).replace(/\r/g, "");
    const lines = cleanedText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) {
      setErrorMsg("Uploaded file seems empty!");
      setPhase("error");
      return;
    }

    if ((lines.length - 1) > maxRows) {
      setErrorMsg(`You can only upload ${maxRows} rows at once! Your file has ${lines.length - 1} rows.`);
      setPhase("error");
      return;
    }

    const headerLine = lines[0];
    const headers = parseCsvLine(headerLine);
    if (!validateHeadersExact(headers)) {
      setErrorMsg("Invalid headers. Please use the provided template.");
      setPhase("error");
      return;
    }

    const parsedRows = [];
    for (let i = 1; i < lines.length; i++) {
      const fields = parseCsvLine(lines[i]);
      const obj = {};
      for (let h = 0; h < requiredHeaders.length; h++) {
        obj[requiredHeaders[h]] = fields[h] !== undefined ? fields[h] : "";
      }
      obj.rowIndex = i + 1;
      parsedRows.push(obj);
    }

    if (!parsedRows.length) {
      setErrorMsg("No rows found in file.");
      setPhase("error");
      return;
    }

    // Check for empty required fields
    const hasEmpty = parsedRows.some((r) =>
      requiredHeaders.some((h) => !r[h] || r[h].toString().trim() === "")
    );
    if (hasEmpty) {
      setErrorMsg("Some rows contain empty fields.");
      setPhase("error");
      return;
    }

    // Custom validation if provided
    let invalid = [];
    let processed = parsedRows;

    if (customValidation) {
      processed = parsedRows.map((row) => {
        const validation = customValidation(row);
        if (!validation.isValid) {
          const rowWithErrors = { ...row, fieldErrors: validation.errors, hasError: true };
          invalid.push(rowWithErrors);
          return rowWithErrors;
        }
        return row;
      });
    }

    if (invalid.length > 0) {
      setInvalidRows(invalid);
      setRows([]);
      setErrorMsg("Some rows contain invalid data. Please check the highlighted cells.");
      setPhase("preview");
      return;
    }

    setInvalidRows([]);
    setRows(processed);
    setErrorMsg("");
    setPhase("uploading");

    // Notify parent component
    if (onDataProcessed) {
      onDataProcessed(processed, []);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (file.type !== "text/csv" && !file.name?.toLowerCase().endsWith(".csv")) {
      setErrorMsg("Invalid file type. Only CSV allowed.");
      setPhase("error");
      return;
    }
    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = async (e) => await parseCSVText(e.target.result);
    reader.readAsText(file);
  };

  const handleFileInput = (e) => {
    const f = e.target.files?.[0];
    handleFile(f);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  useEffect(() => {
    let timer;
    if (phase === "uploading") {
      const baseInterval = 90;
      const sizeFactor = Math.min(6, Math.max(1, (uploadedFile?.size || 0) / (1024 * 1024)));
      let val = 0;
      timer = setInterval(() => {
        val += Math.max(8, Math.round(6 * sizeFactor));
        if (val >= 100) {
          val = 100;
          setProgress(100);
          clearInterval(timer);
          setTimeout(() => setPhase("preview"), 220);
          return;
        }
        setProgress(val);
      }, baseInterval);
    } else {
      setProgress(0);
    }
    return () => clearInterval(timer);
  }, [phase, uploadedFile]);

  const removeUploadedFile = () => {
    setPhase("idle");
    setUploadedFile(null);
    setRows([]);
    setInvalidRows([]);
    setErrorMsg("");
    setProgress(0);
    if (onDataProcessed) {
      onDataProcessed([], []);
    }
  };

  const downloadTemplate = () => {
    const csv = `${requiredHeaders.join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Auto-generate columns if not provided
  const tableColumns = customColumns || requiredHeaders.map((h) => ({
    Header: h,
    accessor: h,
    Cell: ({ row, value, column }) => {
      // Handle empty values with hyphen
      const displayValue = (value === null || value === undefined || value === "") ? "-" : value;
      const shouldTruncate = displayValue.length > 15;

      const content = shouldTruncate ? (
        <Tooltip label={displayValue} hasArrow placement="top" bg="gray.700">
          <span>{ellipsisify(displayValue, 12)}</span>
        </Tooltip>
      ) : (
        <span>{displayValue}</span>
      );

      // Check for field-specific error
      const isFieldInvalid = row.original.fieldErrors && row.original.fieldErrors[column.id];
      const invalidClass = isFieldInvalid ? "invalid-cell-text" : "";

      return <span className={invalidClass}>{content}</span>;
    },
  }));

  return (
    <div className="csv-uploader-container">
      <div className="uploader-box" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
        {(phase === "idle" || phase === "error") && (
          <>
            <p className="uploader-title">
              Don't have the template?
              <span className="downloadTemplate" onClick={downloadTemplate}>
                Click Here!
              </span>
            </p>
            <p className="uploader-note">*Make sure you are using Customer template file</p>

            <label className={`upload-box ${phase === "error" ? "show-overlay" : ""}`}>
              <CloudUploadIcon className="cloud-upload-icon" />
              <p className={`uploader-hint ${phase === "error" ? "error" : ""}`}>
                {phase === "error" ? errorMsg : "Drag file here or Browse for file upload"}
              </p>
              <input type="file" accept=".csv" className="upload-file-input" onChange={handleFileInput} />
            </label>
          </>
        )}

        {phase === "uploading" && (
          <div className="uploaded-file-box">
            <FileIcon className="uploaded-file-icon" />
            <div className="loader-container">
              <Tooltip label={uploadedFile?.name?.length > 30 ? uploadedFile.name : ""}>
                <p className="file-name">{ellipsisify(uploadedFile?.name || "Upload.csv", 30)}</p>
              </Tooltip>
              <div className="loader-bar">
                <div className="progress-bar" style={{ width: `${progress}%` }} />
              </div>
              <div className="file-details">
                <p className="file-size">
                  {uploadedFile?.size ? `${(uploadedFile.size / (1024 * 1024)).toFixed(3)} MB` : ""}
                </p>
                <p className="file-uploaded-hint">
                  {progress < 100 ? "Processing..." : "Preparing preview..."}
                </p>
              </div>
            </div>
            <CloseIcon className="uploaded-file-cross-icon" onClick={removeUploadedFile} />
          </div>
        )}

        {phase === "preview" && (
          <>
            <div className="uploaded-file-box">
              <FileIcon className="uploaded-file-icon" />
              <div className="loader-container">
                <Tooltip label={uploadedFile?.name?.length > 30 ? uploadedFile.name : ""}>
                  <p className="file-name">{ellipsisify(uploadedFile?.name || "Upload.csv", 30)}</p>
                </Tooltip>
                <div className="loader-bar">
                  <div className="progress-bar" style={{ width: "100%" }} />
                </div>
                <div className="file-details">
                  <p className="file-size">
                    {uploadedFile?.size ? `${(uploadedFile.size / (1024 * 1024)).toFixed(3)} MB` : ""}
                  </p>
                  <p className="file-uploaded-hint">
                    {invalidRows.length > 0 ? "Invalid rows detected" : "Uploaded"}
                  </p>
                </div>
              </div>
              <CloseIcon className="uploaded-file-cross-icon" onClick={removeUploadedFile} />
            </div>

            <div className="uploaded-data-container">
              <ReactTable
                columns={tableColumns}
                data={invalidRows.length ? invalidRows.slice(0, 3) : rows.slice(0, 3)}
                pageSize={3}
              />
              {(invalidRows.length > 3 || rows.length > 3) && (
                <div className="preview-btn-container">
                  <Button
                    onClick={() => setShowModalPreview(true)}
                    variant="link"
                    className="file-preview-button"
                  >
                    Preview complete file <ChevronRightIcon />
                  </Button>
                </div>
              )}
            </div>
            {errorMsg && <p className="invalid-msg">{errorMsg}</p>}
          </>
        )}
      </div>

      {showModalPreview && (
        <div className="file-previewModal-overlay">
          <div className="modal-cross-icon-container">
            <RoundCrossIcon
              onClick={() => setShowModalPreview(false)}
              className="modal-cross-icon"
            />
          </div>
          <div className="csv-prev-file-table-container">
            <ReactTable
              columns={tableColumns}
              data={invalidRows.length ? invalidRows : rows}
              pageSize={10000}
              showSearch={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
